import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { TiMetricsActionEventDetail } from '@ticom/web-component-utils';
import DAMImageUtil from '../../global/dam-image-util';
import { default as languageStrings } from '../../global/l10n/OpnSnapshot.json';
import Util from '../../global/util';
import { OPNDetail } from '../opn-details/opn-details.interface';
import OPNDao, { OPNDaoConfig } from './opn-snapshot.dao';
import { OPNMatch, OPNParameters } from './opn-snapshot.interface';

@Component({
    tag: 'ti-opn-snapshot',
    styleUrl: 'opn-snapshot.scss',
    shadow: true
})
export class OpnSnapshot {

    /**
     * Load Statuses
     */
    private _statusType = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    }

    private _dataSheetLabel: string;
    private _snapshotUrl: string;

    /**
     * The element for this component
     */
    @Element() _hostElement: HTMLTiOpnSnapshotElement;

    /**
     * Holds opn response data
     *
     * @type {OPNMatch}
     */
    @State() opnMatchDetails: OPNMatch;

    /**
     * Product page url
     *
     * @type {string}
     */
    @State() productPageUrl: string;

    /**
     * Keep track of the status of the component so that we can render a response to the user accordingly.
     *
     * @type {string}
     * @default StatusType.Loading
     */
    @State() status: string = this._statusType.LOADING;
    @Watch('status')
    handleComponentStatusUpdate(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue === this._statusType.READY) {
            this.componentRendered.emit({
                tag: this._hostElement.tagName
            });
        }
    }

    /**
     * For analytics purposes. Query parameters to add to all outgoing links
     *
     * @type {string}
     * @default 'keyMatch={searchTerm}&tisearch={tisearch}&usecase={usecase}'
     */
    @Prop() commonQueryString: string = 'keyMatch={searchTerm}&tisearch={tisearch}&usecase={usecase}';

    /**
     * Url for fetching custom reel fees
     *
     * @type {string}
     * @default '/search/customReelFees'
     */
    @Prop() customReelFeesApiUrl: string = '/search/customReelFees';

    /**
     * Value for tisearch metrics parameter
     *
     * @type {string}
     * @default 'universal_search'
     */
    @Prop() tisearch: string = 'universal_search';

    /**
     * Default image to be displayed if the image for the gpn is not found.
     * Should be a relative path from the server root.
     *
     * @type {string}
     * @default '/content/dam/ticom/images/icons/svg-icons/no-image-available-icon.svg'
     */
    @Prop() defaultImage: string = '/content/dam/ticom/images/icons/svg-icons/no-image-available-icon.svg';

    /**
     * URL for the image api
     *
     * @type {string}
     * @default '/imagemetadata/api'
     */
    @Prop() imageApiUrl: string = '/imagemetadata/api';

    /**
     * Product model api url
     *
     * @type {string}
     * @default '/productmodel/{gpn}/orderables'
     */
    @Prop() productModelApiUrl: string = '/productmodel/{gpn}/orderables';

    /**
    * Search api url
    *
    * @type {string}
    * @default '/search/opn'
    */
    @Prop() searchApiUrl: string = '/search/opn';

    /**
     * Host URL
     *
     * @type {string}
     * @default ""
     */
    @Prop() tiHost: string = '';

    /**
     * Store host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com"
     */
    @Prop() tiStoreHost: string = 'https://www.ti.com';

    /**
     * Store CN Host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com.cn"
     */
    @Prop() tiStoreHostCn: string = 'https://www.ti.com.cn';

    /**
     * Value of locale
     *
     * @type {string}
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';
    @Watch('locale')
    handleLocaleChange() {
        this._updateLanguageStrings();
    }

    /**
     * OPN value
     *
     * @type {string}
     */
    @Prop() opn: string;
    @Watch('opn')
    handleOpnChange(newValue: string, oldValue: string) {
        if (!newValue || newValue.length === 0) {
            this.status = this._statusType.ERROR;
            return;
        }
        if (newValue !== oldValue) {
            this.status = this._statusType.LOADING;
        }
        this._updateData();
    }

    /**
     * Event emitted for metrics
     *
     * @type {EventEmitter}
     * @memberof TiOpnSnapshot
     */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    /**
     * Event for when component section has rendered
     */
    @Event() componentRendered: EventEmitter;

    /**
     * Lifecycle event handler.
     */
    componentWillLoad() {
        if (this.opn && this.opn.length > 0) {
            this._updateData();
        }
        this._updateLanguageStrings();
    }

    /**
     * Listen to expand/collapse events of disclosure list and emits tiMetricsAction event for the same
     */
    @Listen('tiDisclosureListChange')
    onTiDisclosureListChange(event) {
        const metricsDetail = {
            eventName: 'chevron click',
            elementName: this._hostElement.tagName,
            data: {
                value: event.detail.action,
                column: 'qty_price'
            }
        };
        this.tiMetricsAction.emit(metricsDetail);
    }

    /**
    * Metrics interceptor for add-to-cart so we can emit from the source (OPN Snapshot)
    */
    @Listen('tiMetricsAction')
    onAddToCartTiMetricsAction(event: CustomEvent<TiMetricsActionEventDetail>) {
        if (event.detail.elementName === 'TI-ADD-TO-CART' && event.detail.eventName !== 'add to cart modal close') {
            event.stopPropagation();
            let newMetricsEvent = event.detail;
            newMetricsEvent.elementName = this._hostElement.tagName;
            this.tiMetricsAction.emit(newMetricsEvent);
        }
    }

    /**
     * Catch link clicks and attach analytics query parameters
     * @param event
     */
    @Listen('click')
    clickHandler(event: any) {
        let tag = (event.composedPath && event.composedPath()[0]) || event.target as HTMLElement;
        // chipshot click
        if (tag.className === 'ti-image-object') {
            tag = tag.parentElement.parentElement;
        }
        if (tag.tagName.toLowerCase() === 'a' && tag.getAttribute('href')) {
            event.preventDefault();
            event.stopPropagation(); //Fix to prevent multiple tabs from opening - Tarek - 1/24/2022
            const url = Util.formatUrl(
                tag.getAttribute('href'),
                {
                    searchTerm: this.opnMatchDetails.opn,
                    tisearch: this.tisearch.toLocaleLowerCase(),
                    usecase: this.opnMatchDetails.gpn.hasAlternates ? 'OPN-ALT' : 'OPN'

                },
                this.commonQueryString
            );
            if (tag.getAttribute('target') === '_blank') {
                window.open(url, '_blank');
            } else {
                window.location.href = url;
            }
        }
    }

    @Method()
    public async updateData(opnMatch: OPNMatch) {
        if (opnMatch && opnMatch.opnMatch) {
            this.opnMatchDetails = opnMatch;
            this.status = this._statusType.READY
        }
        else {
            this.status = this._statusType.ERROR;
        }
    }

    private async _updateData() {
        const config: OPNDaoConfig = OPNDao.config(this.tiHost, this.productModelApiUrl, this.searchApiUrl, this.customReelFeesApiUrl);
        this.updateData(await OPNDao.searchOPNMatch(this.opn, this.locale, config));
    }

    /**
     * sets default locale as en-US for invalid locale and updates language strings.
     */
    private _updateLanguageStrings() {
        this.locale = Util.getValidLocale(this.locale);
        this._dataSheetLabel = languageStrings[this.locale]["dataSheet"];
        this.productPageUrl = languageStrings[this.locale]["productPageLink"];
    }

    private _opnDetailsRow(opnParameters: OPNParameters, opnMatch: OPNMatch) {
        const data: OPNDetail = {
            opn: opnParameters.orderablePartNumber,
            gpn: opnMatch.gpn.genericPartNumber,
            customReelPriceList: opnMatch.customReelPriceList,
            currencyExchangeRateMap: opnMatch.currencyExchangeRateMap,
            opnParameters: opnParameters
        };
        return (
            <ti-opn-details
                ti-host={this.tiHost}
                locale={this.locale}
                ti-store-host={this.tiStoreHost}
                ti-store-host-cn={this.tiStoreHostCn}
                opn-detail-data={JSON.stringify(data)}
                usecase='OPN'
                ti-metrics-location='ti.com-opnsearch'
            >
            </ti-opn-details>
        )
    }

    async componentWillRender() {
        if (this.opnMatchDetails && this.opnMatchDetails.gpn && this.opnMatchDetails.gpn.genericPartNumber) {
            this._snapshotUrl = await DAMImageUtil.getInstance(this.imageApiUrl).getFirstImageUrl(this.opnMatchDetails.gpn.genericPartNumber, this.locale, 'gpn', 'IC PACKAGE IMAGE ASSET', 'singlesmall');
        }
        this._snapshotUrl = this._snapshotUrl || `${this.tiHost}${this.defaultImage}`;
    }

    render() {
        if (this.status !== this._statusType.READY) {
            return '';
        }
        const viewProductFamilyUrl = Util.formatUrl(
            this.opnMatchDetails.gpn.gpnFamilyUrl,
            {
                searchTerm: this.opnMatchDetails.opn,
                tisearch: this.tisearch.toLocaleLowerCase(),
                usecase: this.opnMatchDetails.gpn.hasAlternates ? 'OPN-ALT' : 'OPN'
            },
            this.commonQueryString
        );
        return <Host>
            <ti-card appearance="plain-white">
                <div class="ti-opn-snapshot">
                    <div class="ti-opn-snapshot-header">
                        <div class="ti-opn-snapshot-fixed">
                            <ti-product-synopsis
                                product-url={`${this.productPageUrl}${this.opnMatchDetails.gpn.genericPartNumber}`}
                                image-url={this._snapshotUrl}
                                default-image={`${this.tiHost}${this.defaultImage}`}>
                                {this.opnMatchDetails.gpn.newFlag &&
                                    <span slot="new-badge" class="new-badge">{languageStrings[this.locale]["newBadgeLabel"]}</span>
                                }
                                <a slot="product-family" href={this.opnMatchDetails.gpn.gpnFamilyUrl} innerHTML={this.opnMatchDetails.gpn.gpnFamilyName}></a>
                                <a slot="product-link" href={`${this.productPageUrl}${this.opnMatchDetails.gpn.genericPartNumber}`}>{this.opnMatchDetails.gpn.genericPartNumber}</a>
                                <ti-product-status compact slot="product-status"
                                    status={this.opnMatchDetails.gpn.marketingStatusId}
                                    tooltip={this.opnMatchDetails.gpn.statusLongDesc}>
                                    <a data-navtitle="status-badge" href={languageStrings[this.locale]["productStatusLink"]}>
                                        {this.opnMatchDetails.gpn.statusDescription}
                                    </a>
                                </ti-product-status>
                                <a slot="description" href={`${this.productPageUrl}${this.opnMatchDetails.gpn.genericPartNumber}`} innerHTML={this.opnMatchDetails.gpn.deviceDescription}></a>
                                <div slot="bottom">
                                    {this.opnMatchDetails.gpn.datasheetUrl &&
                                        <div class="ti-opn-snapshot-data-sheet">
                                            <span>{this._dataSheetLabel}:</span>
                                            <a data-navtitle="data sheet-pdf" href={this.opnMatchDetails.gpn.datasheetUrl} target="_blank">
                                                PDF
                                            </a>
                                            {this.opnMatchDetails.gpn.htmlDocUrl &&
                                                <div>
                                                    <span class="ti-opn-snapshot-divider">|</span>
                                                    <a data-navtitle="data sheet-html" href={this.opnMatchDetails.gpn.htmlDocUrl} target="_blank">
                                                        HTML
                                                    </a>
                                                </div>
                                            }
                                        </div>
                                    }
                                    {this.opnMatchDetails.gpn.hasAlternates &&
                                        <div class="ti-opn-snapshot-view-alternates">
                                            <ti-selection-tool-view-alternates
                                                gpn={this.opnMatchDetails.gpn.genericPartNumber}
                                                locale={this.locale}
                                                part-number={this.opnMatchDetails.gpn.genericPartNumber}>
                                                <button class="ti_p-iconText">
                                                    <span class="ti_p-iconText-icon">
                                                        <ti-svg-icon appearance="secondary" size="s" icon-set="actions">open-in-new</ti-svg-icon>
                                                    </span>
                                                    {languageStrings[this.locale]['viewAlternatesLabel']}
                                                </button>
                                            </ti-selection-tool-view-alternates>
                                        </div>
                                    }
                                </div>
                            </ti-product-synopsis>
                        </div>
                    </div>
                    <div class="ti-opn-snapshot-opn-match-details">
                        {this._opnDetailsRow(this.opnMatchDetails.opnMatch, this.opnMatchDetails)}
                        {this.opnMatchDetails.opnLike.length ?
                            <span class="ti-opn-snapshot-additional-pkg">
                                {languageStrings[this.locale]["additionalPackageCarrier"]}
                            </span>
                            :
                            null
                        }
                        {this.opnMatchDetails.opnLike.slice(0, 3).map(opnDetails => this._opnDetailsRow(opnDetails, this.opnMatchDetails))}
                    </div>
                    <div>
                        <a data-navtitle="view product family" href={viewProductFamilyUrl} class="ti-opn-snapshot-product-family-link">
                            <ti-svg-icon size="s" appearance="secondary">parametric-filter</ti-svg-icon>
                            <span innerHTML={languageStrings[this.locale]['viewAllLabel'].replace('{0}', this.opnMatchDetails.gpn.gpnFamilyName)}></span>
                        </a>
                    </div>
                </div>
            </ti-card>
            <ti-oss-custom-reel-dialog locale={this.locale}></ti-oss-custom-reel-dialog>
            <ti-oss-sku-dialog locale={this.locale}></ti-oss-sku-dialog>
        </Host>
    }
}
